<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import ParetoDiagram, { type ParetoPoint } from "@/components/datascience/ParetoDiagram.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import type { EstimatorReportDto } from "@/dto";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatNumber, swapItemsInArray } from "@/services/utils";
import { useModalsStore } from "@/stores/modals";
import EstimatorReportComparisonCustomModalBody from "@/views/comparison/EstimatorReportComparisonCustomModalBody.vue";
import EstimatorReportComparisonCustomModalFooter from "@/views/comparison/EstimatorReportComparisonCustomModalFooter.vue";

const { reports, isReadOnly } = defineProps<{
  reports: EstimatorReportDto[];
  isReadOnly: boolean;
}>();

const api = useApi();
const route = useRoute();
const router = useRouter();
const modals = useModalsStore();
const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);

const xMetric = ref("");
const yMetric = ref("");
const hoveredModelId = ref(-1);
const tooltipPosition = ref({ x: -1, y: -1 });
const sortedByMetricName = ref("");
const sortDirection = ref<"asc" | "desc">("asc");

const selectedModelsIds = defineModel<number[]>("selectedModelsIds", { required: true });
const tradeoffExplanations = defineModel<{ reportId: string; explanation: string }[]>(
  "tradeoffExplanations",
  { required: true }
);

const paretoDiagram = useTemplateRef<InstanceType<typeof ParetoDiagram>>("paretoDiagram");

let hideTooltipTimeout = -1;

const timingMetricsNames = computed(() => {
  return reports
    .flatMap((r) => r.metrics)
    .filter((m) => m.name.includes("_time"))
    .sort((a, b) => (b.position ?? -Infinity) - (a.position ?? -Infinity))
    .map((m) => m.name);
});

const statisticalMetricsNames = computed(() => {
  return reports
    .flatMap((r) => r.metrics)
    .filter((m) => !m.name.includes("_time"))
    .sort((a, b) => (b.position ?? -Infinity) - (a.position ?? -Infinity))
    .map((m) => m.name);
});

const metricsNames = computed(() => {
  return [...new Set([...timingMetricsNames.value, ...statisticalMetricsNames.value])];
});

const metricsVerboseNamesMap = computed(() => {
  const namesMap = new Map<string, string>();
  reports
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      namesMap.set(m.name, m.verbose_name);
    });
  return namesMap;
});

const metricsGreaterIsBetterMap = computed(() => {
  const favorabilityMap = new Map<string, boolean>();
  reports
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      favorabilityMap.set(m.name, m.greater_is_better ?? true);
    });
  return favorabilityMap;
});

const pointsOnPlot = computed<ParetoPoint[]>(() =>
  reports
    .map((r) => ({
      id: r.id,
      x: getReportMetricByName(r, xMetric.value),
      y: getReportMetricByName(r, yMetric.value),
      hover: r.id === hoveredModelId.value,
    }))
    .filter((p) => !isNaN(p.x) && !isNaN(p.y))
);

const directions = computed(() => ({
  x: metricsGreaterIsBetterMap.value.get(xMetric.value) ?? true,
  y: metricsGreaterIsBetterMap.value.get(yMetric.value) ?? true,
}));

const selectedModels = computed(() =>
  selectedModelsIds.value.map((id) => reports.find((r) => r.id === id)!)
);

const differenceWithBest = computed(() => {
  const differences = new Map<number, Map<string, { value: string; better: boolean }>>();
  const best = reports.find((r) => r.id === selectedModelsIds.value[0]);
  if (best) {
    for (const model of selectedModels.value) {
      if (model.id === best?.id) {
        continue;
      }
      for (const metric of metricsNames.value) {
        const greaterIsBetter = metricsGreaterIsBetterMap.value.get(metric) ?? true;
        const value = getReportMetricByName(model, metric);
        const bestValue = getReportMetricByName(best, metric);
        if (!differences.has(model.id)) {
          differences.set(model.id, new Map<string, { value: string; better: boolean }>());
        }
        const difference = value - bestValue;
        const formattedDifference = `${difference > 0 ? "+" : ""}${formatNumber(difference)}`;
        differences.get(model.id)!.set(metric, {
          value: formattedDifference,
          better: greaterIsBetter ? difference > 0 : difference < 0,
        });
      }
    }
  }
  return differences;
});

const sortedReports = computed(() => {
  const sortedReports = [...reports];
  if (sortedByMetricName.value !== "") {
    sortedReports.sort((a, b) => {
      const aValue = getReportMetricByName(a, sortedByMetricName.value);
      const bValue = getReportMetricByName(b, sortedByMetricName.value);
      return sortDirection.value === "asc" ? aValue - bValue : bValue - aValue;
    });
  }
  return sortedReports;
});

function getReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const name = metricsVerboseNamesMap.value.get(metricName);
  if (name) {
    const m = report.metrics.find(
      (m) => (m.data_source === "test" || m.data_source === null) && m.name === metricName
    );
    if (m) {
      return m.value;
    }
  }
  return NaN; // 🤷
}

function getFormattedReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const m = getReportMetricByName(report, metricName);
  if (isNaN(m)) {
    return "--"; // 🤷
  }
  return formatNumber(m);
}

function toggleModel(id: number) {
  if (selectedModelsIds.value.includes(id)) {
    selectedModelsIds.value = selectedModelsIds.value.filter((i) => i !== id);
  } else {
    tradeoffExplanations.value.push({ reportId: id.toString(), explanation: "No description." });
    selectedModelsIds.value.push(id);
  }
}

function getExplanation(reportId: number) {
  const explanation = tradeoffExplanations.value.find((e) => e.reportId === reportId.toString());
  return explanation?.explanation ?? "";
}

function getHoveredReportName(reportId: number) {
  const r = reports.find((r) => r.id == reportId);
  if (r) {
    return r.key;
  }
  return "";
}

function onMouseOverPoint(id: number, x: number, y: number) {
  clearTimeout(hideTooltipTimeout);
  hoveredModelId.value = id;
  tooltipPosition.value = { x, y };
}

function onMouseOut() {
  hideTooltipTimeout = window.setTimeout(() => {
    hoveredModelId.value = -1;
    tooltipPosition.value = { x: -1, y: -1 };
  }, 200);
}

function onMouseHoverReport(id: number) {
  const r = reports.find((r) => r.id == id);
  if (r) {
    const x = getReportMetricByName(r, xMetric.value);
    const y = getReportMetricByName(r, yMetric.value);
    if (!isNaN(x) && !isNaN(y)) {
      clearTimeout(hideTooltipTimeout);
      hoveredModelId.value = id;
      if (paretoDiagram.value && id !== -1) {
        tooltipPosition.value = paretoDiagram.value.getCoordinates(id);
      }
    }
  }
}

function moveModelUp(id: number) {
  const index = selectedModelsIds.value.indexOf(id);
  if (index > 0) {
    const swapped = [...selectedModelsIds.value];
    swapItemsInArray(swapped, index, index - 1);
    selectedModelsIds.value = swapped;
  }
}

function moveModelDown(id: number) {
  const index = selectedModelsIds.value.indexOf(id);
  if (index < selectedModelsIds.value.length - 1) {
    const swapped = [...selectedModelsIds.value];
    swapItemsInArray(swapped, index, index + 1);
    selectedModelsIds.value = swapped;
  }
}

function sortReportsByMetric(metricName: string) {
  if (metricName !== sortedByMetricName.value) {
    const greaterIsBetter = metricsGreaterIsBetterMap.value.get(metricName) ?? true;
    if (greaterIsBetter) {
      sortDirection.value = "desc";
    } else {
      sortDirection.value = "asc";
    }
  } else {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  }
  sortedByMetricName.value = metricName;
}

async function saveComparison() {
  const [confirmed, name] = await modals.prompt({
    title: "Add a name to your comparison (1/2)",
    titleIcon: "icon-cube",
    promptedValueName: "Name",
    confirmText: "Next",
  });
  if (confirmed) {
    const explanations = ref<string[]>(
      Array.from({ length: selectedModelsIds.value.length }, () => "")
    );
    const [confirmed] = await modals.openComponentModal<string[]>({
      title: "Add a note to explain your comparison (2/2)",
      titleIcon: "icon-cube",
      body: EstimatorReportComparisonCustomModalBody,
      footer: EstimatorReportComparisonCustomModalFooter,
      models: { explanations },
      bodyProps: {
        items: selectedModels.value.map((m) => ({
          key: m.key,
          metrics: [xMetric.value, yMetric.value].map((metric) => ({
            name: metric,
            value: getFormattedReportMetricByName(m, metric),
            differenceWithBest: differenceWithBest.value.get(m.id)?.get(metric)?.value,
            better: differenceWithBest.value.get(m.id)?.get(metric)?.better,
          })),
        })),
      },
    });
    if (confirmed) {
      const result = await api.createComparisons(
        workspacePublicId.value,
        projectName.value,
        name,
        reports.map((r) => r.id),
        selectedModelsIds.value,
        Object.fromEntries(
          selectedModelsIds.value.map((id, index) => [id, explanations.value[index] ?? ""])
        )
      );
      if (result) {
        router.push({
          name: RouteNames.ProjectDetail,
          params: {
            workspacePublicId: workspacePublicId.value,
            projectName: projectName.value,
            section: ProjectDetailSections.Comparisons,
          },
        });
      }
    }
  }
}

watch(
  () => reports,
  () => {
    xMetric.value = timingMetricsNames.value[0] ?? "";
    yMetric.value = statisticalMetricsNames.value[0] ?? "";
  },
  { immediate: true }
);

defineExpose({
  saveComparison,
});
</script>

<template>
  <div class="estimator-report-comparison" :class="{ 'is-read-only': isReadOnly }">
    <ExpandableCard title="Pareto diagram" icon="icon-dashboard" class="pareto-diagram">
      <template #actions>
        <DropdownButton help-label="x: " :label="xMetric" icon-left="icon-chevron-down">
          <DropdownButtonItem
            v-for="m in metricsNames"
            :key="m"
            :label="metricsVerboseNamesMap.get(m)"
            @click="xMetric = m"
          />
        </DropdownButton>
        <DropdownButton help-label="y: " :label="yMetric" icon-left="icon-chevron-down">
          <DropdownButtonItem
            v-for="m in metricsNames"
            :key="m"
            :label="metricsVerboseNamesMap.get(m)"
            @click="yMetric = m"
          />
        </DropdownButton>
      </template>

      <div class="plot">
        <ParetoDiagram
          ref="paretoDiagram"
          :points="pointsOnPlot"
          :directions="directions"
          @mouse-over-point="onMouseOverPoint"
          @mouse-out-point="onMouseOut"
        />

        <Transition name="fade">
          <div
            v-if="hoveredModelId !== -1 && tooltipPosition.x !== -1 && tooltipPosition.y !== -1"
            class="plot-tooltip shadow-lg"
            :style="{
              transform: `translate(${tooltipPosition.x + 20}px, ${tooltipPosition.y - 20}px)`,
            }"
          >
            {{ getHoveredReportName(hoveredModelId) }}
          </div>
        </Transition>
      </div>
    </ExpandableCard>

    <ExpandableCard title="Selected trade-offs" icon="icon-selected-cube" class="selected-models">
      <Transition name="fade" mode="out-in">
        <div v-if="selectedModelsIds.length === 0" class="placeholder">
          <div class="text-lg-medium">No model selected</div>
          <div class="placeholder-subtitle text-base-regular">
            Select one’s you want to save in the table below.
          </div>
        </div>

        <Simplebar v-else class="scroller">
          <div class="selected-models-list">
            <TransitionGroup name="list">
              <div v-for="model in selectedModels" :key="model.id" class="selected-model-container">
                <div
                  :class="{ hover: model.id === hoveredModelId }"
                  @mouseover="onMouseHoverReport(model.id)"
                  @mouseout="onMouseOut"
                  class="selected-model"
                >
                  <div class="name-and-actions">
                    <div class="name text-base-medium">
                      <div><i class="icon icon-cube" /> {{ model.key }}</div>
                    </div>

                    <div v-if="!isReadOnly" class="actions">
                      <SimpleButton
                        icon-left="icon-arrow-up"
                        kind="ghost"
                        @click="moveModelUp(model.id)"
                        :disabled="selectedModelsIds.indexOf(model.id) === 0"
                      />

                      <SimpleButton
                        icon-left="icon-arrow-down"
                        kind="ghost"
                        @click="moveModelDown(model.id)"
                        :disabled="
                          selectedModelsIds.indexOf(model.id) === selectedModelsIds.length - 1
                        "
                      />
                    </div>
                  </div>

                  <div class="metrics">
                    <div class="text-base-medium">
                      <span class="number">
                        {{ getFormattedReportMetricByName(model, xMetric) }}
                      </span>

                      <span
                        v-if="differenceWithBest.has(model.id)"
                        class="difference"
                        :class="{
                          better: differenceWithBest.get(model.id)?.get(xMetric)?.better,
                        }"
                      >
                        (
                        <!-- Indentation is important here to avoid generation monospace whitespace.-->
                        <span class="number">{{
                          differenceWithBest.get(model.id)?.get(xMetric)?.value ?? ""
                        }}</span>
                        )
                      </span>

                      <span class="metric-name">({{ metricsVerboseNamesMap.get(xMetric) }})</span>
                    </div>

                    <div class="text-base-medium">
                      <span class="number">
                        {{ getFormattedReportMetricByName(model, yMetric) }}
                      </span>

                      <span
                        v-if="differenceWithBest.has(model.id)"
                        class="difference"
                        :class="{
                          better: differenceWithBest.get(model.id)?.get(xMetric)?.better,
                        }"
                      >
                        (
                        <!-- Indentation is important here to avoid generation monospace whitespace.-->
                        <span class="number">{{
                          differenceWithBest.get(model.id)?.get(yMetric)?.value ?? ""
                        }}</span>
                        )
                      </span>

                      <span class="metric-name">({{ metricsVerboseNamesMap.get(yMetric) }})</span>
                    </div>
                  </div>

                  <div class="explanation paragraph-s">
                    <div><i class="icon icon-document" /></div>
                    <div class="text">
                      <MarkdownWidget :source="getExplanation(model.id)" />
                    </div>
                  </div>
                </div>
              </div>
            </TransitionGroup>
          </div>
        </Simplebar>
      </Transition>
    </ExpandableCard>

    <div class="all-models">
      <div class="title">
        <div class="text-lg-medium">Models comparison</div>
        <div class="subtitle text-base-medium">
          Select the models you want to have as trade-offs
        </div>
      </div>

      <div class="scroller-wrapper">
        <Simplebar class="all-models-scroller">
          <table class="models-table">
            <thead>
              <tr>
                <th v-if="!isReadOnly"></th>
                <th class="text-base-medium">Report name</th>
                <th
                  v-for="m in metricsNames"
                  :key="m"
                  class="text-base-medium"
                  :class="{ sorted: sortedByMetricName === m }"
                >
                  <span class="metric-table-header">
                    {{ metricsVerboseNamesMap.get(m) }}

                    <SimpleButton
                      :icon-left="
                        sortedByMetricName === m
                          ? sortDirection === 'asc'
                            ? 'icon-sort-up'
                            : 'icon-sort-down'
                          : 'icon-sort'
                      "
                      kind="ghost"
                      @click="sortReportsByMetric(m)"
                    />
                  </span>
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="r in sortedReports"
                :key="r.id"
                class="report"
                @mouseover="onMouseHoverReport(r.id)"
                @mouseleave="onMouseOut"
                :class="{ hover: hoveredModelId === r.id }"
              >
                <td v-if="!isReadOnly">
                  <SimpleButton
                    class="table-button"
                    :label="selectedModelsIds.includes(r.id) ? '-' : '+'"
                    kind="ghost"
                    @click="toggleModel(r.id)"
                  />
                </td>

                <td class="text-base-regular">
                  <div class="name text-base-medium">
                    <div><i class="icon icon-cube" /> {{ r.key }}</div>
                  </div>
                </td>

                <td
                  v-for="m in metricsNames"
                  :key="m"
                  class="text-base-regular metric-table-cell"
                  :class="{ sorted: sortedByMetricName === m }"
                >
                  <span
                    v-if="isNaN(getReportMetricByName(r, m)) && (m == xMetric || m == yMetric)"
                    class="warning"
                  >
                    <FloatingTooltip
                      text="This metric has not been calculated, this model will not appear in the plot."
                      placement="top"
                    >
                      <i class="icon icon-warning" />
                    </FloatingTooltip>
                  </span>
                  {{ getFormattedReportMetricByName(r, m) }}
                </td>

                <td>
                  <RouterLink
                    class="table-button skore-button ghost"
                    :to="{
                      name: RouteNames.EstimatorReportDetail,
                      params: { workspacePublicId, projectName, reportId: r.id },
                    }"
                    target="_blank"
                  >
                    Open <i class="icon left icon-external-link" />
                  </RouterLink>
                </td>
              </tr>
            </tbody>
          </table>
        </Simplebar>
      </div>
    </div>
  </div>
</template>

<style scoped>
.estimator-report-comparison {
  display: grid;
  height: 100%;
  gap: var(--numbers-32);
  grid-template:
    "top-left top-right" 50%
    "bottom bottom" 50% / 1fr 1fr;

  :deep(.card-content) {
    min-height: 0;
    max-height: 100%;
    flex-grow: 1;
  }

  .name-and-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .name {
      > div {
        display: inline-block;
        padding: var(--numbers-4) var(--numbers-6);
        border: var(--stroke-width-md) solid var(--border-tertiary);
        border-radius: var(--numbers-8);
        background-color: var(--background-primary);
      }

      .icon {
        color: var(--content-brand-2);
        vertical-align: middle;
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      gap: var(--numbers-8);

      .skore-button {
        --height: var(--numbers-32);

        padding: var(--numbers-16) 0;
      }
    }
  }

  .pareto-diagram {
    grid-area: top-left;

    .plot {
      position: relative;
      z-index: 2;
      display: flex;
      height: 100%;
      flex-direction: column;

      .plot-tooltip {
        position: absolute;
        top: 0;
        left: 0;
        width: max-content;
        padding: var(--numbers-6);
        border-radius: var(--numbers-4);
        background-color: var(--background-inverse);
        color: var(--content-primary-inverse);
        transition: transform var(--animation-duration) var(--animation-easing);
      }
    }
  }

  .selected-models {
    display: flex;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    grid-area: top-right;

    .placeholder {
      display: flex;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--content-secondary);
      gap: var(--numbers-8);
      text-align: center;

      .placeholdersubtitle {
        color: var(--content-secondary);
      }
    }

    .scroller {
      max-height: 100%;
    }

    :deep(.simplebar-track.simplebar-vertical) {
      transform: translateX(12px);
    }

    .selected-models-list {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-16);
    }

    .selected-model {
      display: flex;
      flex-direction: column;
      padding: var(--numbers-12);
      border: var(--stroke-width-md) solid var(--border-tertiary);
      border-radius: var(--numbers-8);
      background-color: var(--background-secondary);
      gap: var(--numbers-12);
      transition: border-color var(--animation-duration) var(--animation-easing);

      &.hover {
        border-color: var(--border-notice);
      }

      .metrics {
        display: flex;
        gap: var(--numbers-12);

        .metric-name {
          padding-left: var(--numbers-4);
          color: var(--content-secondary);
        }
      }

      .explanation {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: var(--numbers-8);

        .text {
          display: -webkit-box;
          overflow: hidden;
          max-height: calc(1rem * 1.5 * 3);
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          line-clamp: 3;
        }
      }
    }

    .difference {
      color: var(--content-negative);

      &.better {
        color: var(--content-positive);
      }
    }
  }

  .all-models {
    display: flex;
    height: 100%;
    flex-direction: column;
    padding-bottom: var(--numbers-32);
    grid-area: bottom;

    .subtitle {
      margin-bottom: var(--numbers-16);
      color: var(--content-secondary);
    }

    .scroller-wrapper {
      overflow: hidden;
      max-height: 100%;
      flex-grow: 1;
      border-radius: var(--numbers-4);
    }

    .all-models-scroller {
      max-height: 100%;
    }

    .models-table {
      position: relative;
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        position: sticky;
        z-index: 2;
        top: 0;

        tr {
          color: var(--content-secondary);

          th {
            padding: var(--numbers-6) var(--numbers-8);
            border: var(--stroke-width-md) solid var(--border-tertiary);
            border-right: var(--stroke-width-md) solid var(--border-tertiary);
            background-color: var(--background-tertiary);
            text-align: left;
            transition: color var(--animation-duration) var(--animation-easing);

            &:first-child {
              border-top-left-radius: var(--numbers-16);
            }

            &:last-child {
              border-top-right-radius: var(--numbers-16);
            }

            &.sorted {
              color: var(--content-brand);
            }

            .metric-table-header {
              display: inline-flex;
              width: 100%;
              align-items: center;
              justify-content: end;
              gap: var(--numbers-8);

              .skore-button {
                --height: var(--numbers-16);

                padding: var(--numbers-8) var(--numbers-4);
                border: none;
                background-color: transparent;
              }
            }
          }
        }
      }

      tbody {
        tr {
          background-color: var(--background-primary);
          transition: background-color var(--animation-duration) var(--animation-easing);

          td {
            padding: var(--numbers-10);
            border-width: var(--stroke-width-md);
            border-style: none solid solid none;
            border-color: var(--border-tertiary);
            transition: background-color var(--animation-duration) var(--animation-easing);

            &.sorted {
              background-color: var(--background-info-subtle);
            }

            &.metric-table-cell {
              text-align: right;
            }

            &:first-child {
              border-left-style: solid;
              border-left-color: var(--border-tertiary);
              text-align: center;
            }

            &:last-child {
              border-right-color: var(--border-tertiary);
              text-align: center;
            }
          }

          &:last-child td:first-child {
            border-bottom-left-radius: var(--numbers-16);
          }

          &:last-child td:last-child {
            border-bottom-right-radius: var(--numbers-16);
          }

          &.hover {
            background-color: var(--background-notice-subtle);

            td.sorted {
              background-color: var(--background-notice-subtle);
            }
          }
        }
      }
    }

    .warning {
      .icon {
        vertical-align: text-top;
      }
    }

    .table-button {
      min-width: auto;
      height: auto;
      padding: var(--numbers-4) var(--numbers-12);

      .icon {
        padding-left: var(--numbers-4);
      }
    }
  }

  &.is-read-only {
    .all-models {
      .models-table {
        tbody {
          tr {
            td {
              &:first-child {
                text-align: left;
              }
            }
          }
        }
      }
    }
  }
}

@supports (animation-timeline: scroll()) {
  .models-table thead {
    animation: scroll-shadow linear both;
    animation-range: 0 calc(32px + (var(--numbers-8) * 2));
    animation-timeline: scroll();
  }

  @keyframes scroll-shadow {
    from {
      box-shadow: none;
    }

    to {
      box-shadow:
        0 4px 8px -2px rgb(16 24 40 / 10%),
        0 2px 4px -2px rgb(16 24 40 / 6%);
    }
  }
}

@supports (animation-timeline: view()) {
  /* try to hide the row when it is scrolled away */
  .models-table tbody tr {
    animation-duration: 0.2s;
    animation-fill-mode: both;
    animation-name: row-exit-animation;
    animation-range: exit;
    animation-timeline: view();
  }

  @keyframes row-exit-animation {
    from {
      opacity: 1;
    }

    to {
      opacity: 0;
    }
  }
}
</style>
